import { Injectable } from '@angular/core';
import { oneSecondMilli } from '../constants-types/constants';
import { PI } from '../constants-types/PI.model';
import { FilterInfo } from '../core-data-model/FilterInfo.model';
import { SearchState } from '../core-data-model/SearchState.model';
import { SortInfo } from '../core-data-model/SortInfo.model';
import { User, UserUpdate } from '../core-data-model/User.model';
import {
    UserUpdateServer,
    UserSearchInfoServer,
    UserAccessUpdateServer,
    UserCreateAccountProvisioningServer
} from '../core-data-model/UserApi.serverinterface';
import { ConsoleLogger } from '../core-utilities/ConsoleLogger';
import { ApiService } from '../system-services/Api.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { PollingService } from './Polling.service';
import { UserV4Transformer } from './UserApi.datatransform';
import { UserQueryGeneratorService } from './UserQueryGenerator.service';

export type UserCreationStatus = {
    requestStatus: 'InProgress' | 'Failed' | 'Completed';
};

// I used strings here because with strings the TYPE system knows while the compilation
// system can be ignorant of this
export type UserSearchOption = 'users' | 'thirdParty' | 'all';

@Injectable({
    providedIn: 'root'
})
export class UserApi {
    constructor(
        private api: ApiService,
        private userQueryGeneratorService: UserQueryGeneratorService,
        private transformV4: UserV4Transformer,
        private userProfile: UserProfileSettingsService,
        private pollService: PollingService
    ) {}
    /*
    SPA currently supports user creation in 2 ways:
        1. Quick User Creation (Classic Way, hits V1 version of APIs)
        2. Through Administration (Modern Way, hits V4 version of APIs)
        TODO: The classic way needs to be wrapped by an adapter service to use the modern API call
    */

    // ==================  V4 APIs  =========================

    updateUserLastLogin(): Promise<void> {
        return this.api
            .patch(`/api/v4/user/updatelastlogintime`, null)
            .then(([response]) => response);
    }

    createUpdateUser(
        user: UserUpdate,
        companyId = this.userProfile.currentCompany
    ) {
        if (user.originalUser) {
            const userUpdateServer =
                this.transformV4.updateClientToServer(user);
            return this.api
                .patch<UserUpdateServer>(
                    `/api/v4/companies/${companyId}/user/update/${user.originalUser.id}`,
                    userUpdateServer
                )
                .then(([response]) => response);
        } else {
            return this.createAccountProvisioningUser(user);
        }
    }

    private createAccountProvisioningUser(user: UserUpdate) {
        let statusUrl = null;
        const userCreateAccountProvisioningServer =
            this.transformV4.createClientToAccountProvisioningServer(user);

        const getUserCreationStatus = async () => {
            const res = await this.api.get<UserCreationStatus>(
                null,
                null,
                statusUrl
            );
            return res[0];
        };

        const resultCheck = (res: UserCreationStatus) =>
            !!res &&
            (res.requestStatus === 'Completed' ||
                res.requestStatus === 'Failed');

        return this.api
            .post<UserCreateAccountProvisioningServer>(
                `/api/v4/provisioning/pisoftware`,
                userCreateAccountProvisioningServer,
                `${PI.appSettings.accountManagementApiUrl}`
            )
            .then(([response, httpResp]) => {
                // User Creation Message placed on Account Provision Pipeline successfully
                // Begin Polling for the status
                statusUrl = httpResp.headers.get('location');
                return this.pollService
                    .createPoll({
                        fn: getUserCreationStatus,
                        terminator: resultCheck,
                        interval: oneSecondMilli * 3,
                        maxAttempts: 20
                    })
                    .then((res: UserCreationStatus) => {
                        if (!res || res.requestStatus === 'Failed') {
                            // Log Failed User's request details in SumoLogic
                            ConsoleLogger.log('userDetails', user);
                            return Promise.reject('Failed to create New User');
                        } else {
                            return Promise.resolve(res);
                        }
                    });
            });
    }

    getUser(
        userId: string,
        companyId = this.userProfile.currentCompany
    ): Promise<User> {
        return this.api
            .get<any>(`/api/v4/companies/${companyId}/user/${userId}`)
            .then(([response]) => {
                if (response) {
                    return this.transformV4.userInfoServerToClient(response);
                }
                return null;
            });
    }

    validateEmail(
        emailAddress: string,
        userId?: string,
        companyId = this.userProfile.currentCompany
    ): Promise<boolean> {
        const encodedEmailAddress = encodeURIComponent(`${emailAddress}`);
        return userId
            ? this.api
                  .get<boolean>(
                      `/api/v4/companies/${companyId}/user/${userId}/email?emailAddress=${encodedEmailAddress}`
                  )
                  .then(([response]) => response)
            : this.api
                  .get<boolean>(
                      `/api/v4/companies/${companyId}/user/email?emailAddress=${encodedEmailAddress}`
                  )
                  .then(([response]) => response);
    }

    getApiKey(
        userId: string,
        companyId = this.userProfile.currentCompany
    ): Promise<string> {
        return this.api
            .get<string>(`/api/v4/companies/${companyId}/user/${userId}/apikey`)
            .then(([response]) => response || null);
    }

    updateApiKey(
        userId: string,
        companyId = this.userProfile.currentCompany
    ): Promise<string> {
        return this.api
            .post<any>(`/api/v4/companies/${companyId}/user/apikey`, {
                userId: userId
            })
            .then(([response]) => response);
    }

    updateUserAccess(
        userId: string,
        isThirdParty: boolean,
        isEnabled: boolean,
        companyId = this.userProfile.currentCompany
    ) {
        const userAccessUpdate: UserAccessUpdateServer = {
            userId: userId,
            isThirdParty: isThirdParty,
            isEnabled: isEnabled
        };
        return this.api
            .patch<UserAccessUpdateServer>(
                `/api/v4/companies/${companyId}/user/updateaccess`,
                userAccessUpdate
            )
            .then(([response]) => response);
    }

    searchUsers(
        pristineSearchState: SearchState,
        isThirdPartyUserSearch: UserSearchOption = 'users',
        companyId = this.userProfile.currentCompany
    ): Promise<User[]> {
        const oDataQuery =
            this.userQueryGeneratorService.createODataString(
                pristineSearchState
            ) + '&$count=true';
        let searchEndpoint = 'usersearch';
        switch (isThirdPartyUserSearch) {
            case 'all':
                searchEndpoint = 'allusersearch';
                break;
            case 'thirdParty':
                searchEndpoint = 'thirdpartyusersearch';
                break;
            case 'users':
                searchEndpoint = 'usersearch';
                break;
        }

        return this.api
            .get<UserSearchInfoServer>(
                `/api/v4/companies/${companyId}/${searchEndpoint}?${oDataQuery}`
            )
            .then(([results]) => {
                if (!results) {
                    pristineSearchState.pageState.maxResults = 0;
                    return [];
                }
                // Temporary hack as server is still not returning total count
                pristineSearchState.pageState.maxResults =
                    results['@odata.count'];
                return results.value.map((res) =>
                    this.transformV4.userInfoServerToClient(res)
                );
            });
    }

    getUsers(
        resultCount = 10,
        filters?: FilterInfo[],
        isThirdParty: UserSearchOption = 'users',
        sort?: SortInfo[]
    ): Promise<User[]> {
        const searchFactors: SearchState = new SearchState(
            {
                resultsPerPage: resultCount,
                currentPageNumber: 1,
                maxResults: resultCount
            },
            filters,
            sort
        );
        return this.searchUsers(searchFactors, isThirdParty);
    }

    async validateUserByEmail(email: string, shareLinkId: string) {
        const [response] = await this.api.post(
            `/api/v5/user/validate/${shareLinkId}`,
            { email },
            PI.appSettings.designApiUrl
        );
        return response;
    }

    async createUserFromShareLink(
        email: string,
        firstName: string,
        lastName: string,
        jobTitle: string,
        ShareLinkId: string
    ): Promise<string> {
        return this.api
            .post(
                `/api/v5/sharelink/register/${ShareLinkId}`,
                {
                    email,
                    firstName,
                    lastName,
                    jobTitle
                },
                PI.appSettings.designApiUrl
            )
            .then(([response]) => Promise.resolve(response));
    }
}
